// hooks/usePriorityQueue.js
import { useState, useEffect, useCallback } from 'react';
import supabase from '../../../lib/supabaseClient';

// leads.marked_hot_at is `timestamp without time zone` — it holds UTC but arrives with
// no offset ("2026-08-01T00:43:14.402"). Bare new Date() parses that as the VIEWER'S
// LOCAL time, which drags every hot lead forward by the browser's UTC offset: on the
// US east coast everything hot in the last 4h reads "now" and the urgent badge counts
// leads up to 5h old. Normalize to UTC first, same as DashboardV2 and LeadDetail do.
// Sibling columns (last_message_at, priority_queue_dismissed_at) ARE timestamptz and
// carry an offset, so only append 'Z' when one is absent.
const asDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const s = String(v).replace(' ', 'T');
  return new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(s) ? s : s + 'Z');
};
const asMs = (v) => { const d = asDate(v); return d ? d.getTime() : 0; };

// Tenant timezone (platform_settings key 'timezone'). Half the live tenants store a bare
// abbreviation ('EST', 'CST') rather than IANA, and PG treats those as fixed-offset with
// no DST — so always map before handing anything to Intl. Mirrors DashboardV2's toIANA.
const TZ_ABBR = {
  EST: 'America/New_York', EDT: 'America/New_York', CST: 'America/Chicago', CDT: 'America/Chicago',
  MST: 'America/Denver', MDT: 'America/Denver', PST: 'America/Los_Angeles', PDT: 'America/Los_Angeles',
  HST: 'Pacific/Honolulu', AKST: 'America/Anchorage', AKDT: 'America/Anchorage',
};
const DEFAULT_TZ = 'America/New_York';
const toIANA = (tz) => {
  if (!tz) return DEFAULT_TZ;
  const mapped = TZ_ABBR[String(tz).toUpperCase()] || String(tz);
  try { new Intl.DateTimeFormat('en-US', { timeZone: mapped }); return mapped; }
  catch { return DEFAULT_TZ; }
};

// Minutes to ADD to UTC to get wall-clock time in `tz` at instant `at`.
function tzOffsetMinutes(tz, at) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(at);
    const p = {};
    for (const { type, value } of parts) p[type] = value;
    const hour = Number(p.hour) === 24 ? 0 : Number(p.hour);
    const wall = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), hour, Number(p.minute), Number(p.second));
    return Math.round((wall - at.getTime()) / 60000);
  } catch {
    return 0;
  }
}

/** Tenant-local calendar date of an instant, as [year, month, day] (month is 1-based). */
function localYMD(at, tz) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
      .format(at).split('-').map(Number);
  } catch {
    return [at.getUTCFullYear(), at.getUTCMonth() + 1, at.getUTCDate()];
  }
}

/** Calendar arithmetic on a local date. Pure Y/M/D math, so DST can't shift the result. */
function shiftDays([y, m, d], delta) {
  const t = new Date(Date.UTC(y, m - 1, d + delta));
  return [t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()];
}

/** UTC instant (ms) at which the tenant-local day [y, m, d] begins. */
function startOfLocalDay([y, m, d], tz) {
  // Treat the wall clock as UTC, then subtract the zone's offset. Re-check the offset at
  // the corrected instant: on a spring-forward date the two disagree and the second wins.
  const wall = Date.UTC(y, m - 1, d);
  const first = tzOffsetMinutes(tz, new Date(wall));
  const ts = wall - first * 60000;
  const second = tzOffsetMinutes(tz, new Date(ts));
  return second === first ? ts : wall - second * 60000;
}

// marked_hot_at is a NAIVE column, so bounds must be naive too. PG would drop the offset
// off a 'Z'-suffixed literal anyway, but stripping it keeps the intent explicit: these are
// UTC wall-clock instants being compared against UTC wall-clock values.
const naiveUTC = (ms) => new Date(ms).toISOString().replace('Z', '');

/**
 * Maps a dateRange key to a { gte, lt } pair of naive-UTC strings for query filtering.
 * Boundaries are TENANT-LOCAL midnights, not UTC midnights: "today" for an EST tenant has
 * to start at their midnight, or leads that go hot after 7pm land in the following day and
 * vanish from the range the tenant is actually looking at.
 * Returns { gte: null, lt: null } for 'allTime' (no filter).
 */
function getDateRangeFilter(dateRange, tz) {
  const today = localYMD(new Date(), tz);
  const sod = (ymd) => naiveUTC(startOfLocalDay(ymd, tz));
  const startOfToday = sod(today);
  const startOfTomorrow = sod(shiftDays(today, 1));
  const trailing = (days) => ({ gte: sod(shiftDays(today, -(days - 1))), lt: startOfTomorrow });

  switch (dateRange) {
    case 'today':
      return { gte: startOfToday, lt: startOfTomorrow };
    case 'yesterday':
      return { gte: sod(shiftDays(today, -1)), lt: startOfToday };
    case 'last7':
      return trailing(7);
    case 'last30':
      return trailing(30);
    case 'thisMonth':
      return { gte: sod([today[0], today[1], 1]), lt: startOfTomorrow };
    case 'lastMonth': {
      const prev = today[1] === 1 ? [today[0] - 1, 12, 1] : [today[0], today[1] - 1, 1];
      return { gte: sod(prev), lt: sod([today[0], today[1], 1]) };
    }
    case 'allTime':
      return { gte: null, lt: null };
    default:
      return trailing(7);
  }
}

const usePriorityQueue = (limit = 10, selectedTenant = null, dateRange = 'last7', channel = 'sms', selectedCampaign = 'all') => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // null = not resolved yet. The date-range bounds depend on it, so the first fetch waits
  // rather than querying UTC boundaries and snapping to the right ones a moment later.
  const [tenantTz, setTenantTz] = useState(null);

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const then = asDate(timestamp);
    if (!then) return '';
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  const isUrgent = (lead) => {
    if (!lead.marked_hot_at) return false;
    // A negative value now means only browser-vs-PG clock skew of a few seconds, and that
    // lead is as new as they come — so it stays urgent. Before the parse fix it meant
    // "within one UTC offset of now", which is what flagged 5-hour-old leads as urgent.
    const hoursSinceHot = (Date.now() - asMs(lead.marked_hot_at)) / (1000 * 60 * 60);
    return hoursSinceHot < 1;
  };

// A lead earns a spot on the priority card whenever we've handed it to a human:
// a hot handoff, an info-send-request, a callback request, or any other reason we
// alerted the tenant about. ANY handoff_triggered lead the tenant was notified of
// belongs here — not just info requests. Keep this in sync with the fetch filter +
// realtime listener below.
const isHandoff = (lead) => lead?.handoff_triggered === true;

// A lead the tenant explicitly X'd off the queue stays hidden UNTIL a NEW inbound
// reply arrives after the dismissal. `awaiting_human_reply` flips true on a fresh
// inbound to a handed-off lead (and a human reply clears it), and last_message_at
// bumps with it — so a newer last_message_at while awaiting means "they replied
// again, bring it back." A rep replying (awaiting cleared) keeps it hidden.
const isDismissed = (lead) => {
  if (!lead?.priority_queue_dismissed_at) return false;
  // Both columns are timestamptz and arrive with an offset, so asMs is a pass-through here.
  // Used anyway so nothing in this file parses a timestamp two different ways.
  const dismissedAt = asMs(lead.priority_queue_dismissed_at);
  const hasNewReply =
    !!lead.awaiting_human_reply &&
    !!lead.last_message_at &&
    asMs(lead.last_message_at) > dismissedAt;
  return !hasNewReply;
};

// Human-readable tag + fallback snippet per handoff reason. Unknown/future reasons
// still surface (generic tag) so a new hot_reason never silently vanishes from the queue.
const HANDOFF_TAGS = {
  info_request: 'Info request',
  callback_request: 'Callback',
  booking_stalled: 'Stalled booking',
};
const handoffFallbackSnippet = (lead) => {
  switch (lead?.hot_reason) {
    case 'info_request': return 'Asked us to send info — needs a human to follow up';
    case 'callback_request': return 'Asked us to call them — needs a human to follow up';
    case 'booking_stalled': return 'Was offered times, never confirmed one — needs a personal nudge';
    default: return 'Handed off — needs a human to follow up';
  }
};

const transformLead = (lead) => {
  const handoff = isHandoff(lead);
  return {
    id: lead.id,
    name: lead.first_name && lead.last_name
      ? `${lead.first_name} ${lead.last_name}`
      : lead.name || 'Unknown',
    business: lead.custom_fields?.['Company Name'] || null,
    campaignName: lead.campaigns?.name || null,
    kind: handoff ? (lead.hot_reason || 'handoff') : 'hot',
    // Tag shown on the card. Only handoffs get a reason tag; a plain hot lead has none.
    label: handoff ? (HANDOFF_TAGS[lead.hot_reason] || 'Needs follow-up') : null,
    snippet: handoff
      ? (lead.latest_judge_reasoning || handoffFallbackSnippet(lead))
      : (lead.latest_judge_reasoning || lead.escalation_reason || 'Marked as hot lead'),
    time: formatTimeAgo(lead.marked_hot_at),
    markedHotAt: lead.marked_hot_at,
    urgent: isUrgent(lead),
    awaitingReply: !!lead.awaiting_human_reply,
    // Compound-state surfacing: a lead can be a hot/booked handoff AND have an open request
    // for materials. hot_reason only carries ONE label, so a booked lead's info ask (hot_reason
    // = 'appointment_booked') is invisible without this. Lets the card show "also wants info".
    infoRequested: lead.info_request_pending === true,
    infoEmail: lead.info_request_email || null
  };
};

  // Tenant timezone from platform_settings, which is where the rest of the app reads it.
  // On 'all' (global admin) there is no single tenant zone to honour, so fall back to the
  // platform default rather than the viewer's browser zone.
  useEffect(() => {
    let cancelled = false;
    if (!selectedTenant || selectedTenant === 'all') { setTenantTz(DEFAULT_TZ); return; }
    // Hold the panel in its loading state while the zone resolves, so switching tenants
    // doesn't leave the previous tenant's queue on screen looking current.
    setTenantTz(null);
    setLoading(true);
    supabase
      .from('platform_settings')
      .select('value')
      .eq('key', 'timezone')
      .eq('tenant_id', selectedTenant)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const raw = data?.value;
        const val = typeof raw === 'string' ? raw : (raw && typeof raw === 'object' ? (raw.value || raw.timezone) : raw);
        setTenantTz(toIANA(val));
      }, () => { if (!cancelled) setTenantTz(DEFAULT_TZ); });
    return () => { cancelled = true; };
  }, [selectedTenant]);

  const fetchQueue = useCallback(async () => {
    // Wait for the zone before querying — the range bounds are built from it.
    if (!tenantTz) return;
    try {
      setLoading(true);

      // Check if this is a demo tenant (skip for 'all')
      if (selectedTenant && selectedTenant !== 'all') {
        const { data: tenantData } = await supabase
          .from('tenants')
          .select('is_demo, industry, industries(name)')
          .eq('id', selectedTenant)
          .single();

        if (tenantData?.is_demo) {
          const industryName = (tenantData.industries?.name || tenantData.industry || '').toLowerCase();
          console.log(`[usePriorityQueue] Demo tenant detected (industry: ${industryName || 'default'}) - returning demo queue`);

          const staffingQueue = [
            {
              id: 'bb7d354b-97bf-4327-80f6-ff68d717fe53',
              name: 'Sarah Mitchell',
              business: 'Bright Smile Dental',
              snippet: 'Interested in dental assistant placement - needs someone ASAP',
              time: '2m',
              markedHotAt: new Date(Date.now() - 2 * 60000).toISOString(),
              urgent: true
            },
            {
              id: 'b8299a6e-5dab-47fb-b83b-03c0395d89f6',
              name: 'Dr. James Wilson',
              business: 'Wilson Family Practice',
              snippet: 'Looking for front desk staff, ready to interview this week',
              time: '15m',
              markedHotAt: new Date(Date.now() - 15 * 60000).toISOString(),
              urgent: true
            },
            {
              id: '9c6ba40c-ddc4-40f0-ad80-309c774d3c94',
              name: 'Amanda Torres',
              business: 'Lakeside Orthodontics',
              snippet: 'Needs 2 hygienists for new location opening next month',
              time: '45m',
              markedHotAt: new Date(Date.now() - 45 * 60000).toISOString(),
              urgent: false
            },
            {
              id: 'fad5e039-741a-4744-8296-0bdf61564fe7',
              name: 'Michael Chen',
              business: 'Premier Dental Group',
              snippet: 'Expanding practice, interested in staffing partnership',
              time: '1h',
              markedHotAt: new Date(Date.now() - 60 * 60000).toISOString(),
              urgent: false
            },
            {
              id: '58131ac3-a70a-486c-a4ca-7f4867b631d7',
              name: 'Lisa Rodriguez',
              business: 'Smile Care Associates',
              snippet: 'Urgent need for temporary coverage next week',
              time: '2h',
              markedHotAt: new Date(Date.now() - 120 * 60000).toISOString(),
              urgent: false
            }
          ];

          const realEstateQueue = [
            {
              id: 'bb7d354b-97bf-4327-80f6-ff68d717fe53',
              name: 'Sarah Mitchell',
              business: '2214 Westlake Dr',
              snippet: 'Inherited the property, vacant 8 months - wants a cash offer this week',
              time: '2m',
              markedHotAt: new Date(Date.now() - 2 * 60000).toISOString(),
              urgent: true
            },
            {
              id: 'b8299a6e-5dab-47fb-b83b-03c0395d89f6',
              name: 'James Wilson',
              business: '807 MacArthur Ave',
              snippet: 'Behind on payments, auction date set - asking how fast we can close',
              time: '15m',
              markedHotAt: new Date(Date.now() - 15 * 60000).toISOString(),
              urgent: true
            },
            {
              id: '9c6ba40c-ddc4-40f0-ad80-309c774d3c94',
              name: 'Amanda Torres',
              business: '145 Bridgeport Ln',
              snippet: 'Tired landlord, tenant moved out - will take as-is, no repairs',
              time: '45m',
              markedHotAt: new Date(Date.now() - 45 * 60000).toISOString(),
              urgent: false
            },
            {
              id: 'fad5e039-741a-4744-8296-0bdf61564fe7',
              name: 'Michael Chen',
              business: '39 Oakridge Ct',
              snippet: 'Relocating for work in 30 days - needs to close before the move',
              time: '1h',
              markedHotAt: new Date(Date.now() - 60 * 60000).toISOString(),
              urgent: false
            },
            {
              id: '58131ac3-a70a-486c-a4ca-7f4867b631d7',
              name: 'Lisa Rodriguez',
              business: '6120 Sunset Blvd',
              snippet: 'Fire damage, wont pass inspection - open to an as-is assignment',
              time: '2h',
              markedHotAt: new Date(Date.now() - 120 * 60000).toISOString(),
              urgent: false
            }
          ];

          const demoQueue = industryName.includes('real estate')
            ? realEstateQueue
            : staffingQueue;

          setQueue(demoQueue);
          setLoading(false);
          return;
        }
      }

      const { gte: rangeStart, lt: rangeEnd } = getDateRangeFilter(dateRange, tenantTz);

      let query = supabase
        .from('leads')
        .select(`
          id,
          name,
          first_name,
          last_name,
          status,
          custom_fields,
          latest_judge_reasoning,
          escalation_reason,
          marked_hot_at,
          last_message_at,
          awaiting_human_reply,
          hot_reason,
          handoff_triggered,
          info_request_pending,
          info_request_email,
          priority_queue_dismissed_at,
          tenant_id,
          campaign_id,
          campaigns!campaign_id(name)
        `)
        .or('status.ilike.%hot%,latest_judge_is_hot.eq.true,handoff_triggered.eq.true')
        // Drop rows the tenant permanently dismissed; the ones still awaiting a human
        // reply are kept here and refined client-side (isDismissed) so a NEW reply
        // re-surfaces them but an already-awaiting dismissal stays hidden.
        .or('priority_queue_dismissed_at.is.null,awaiting_human_reply.eq.true')
        .not('status', 'in', '("Lost","Won","Disqualified","dead")')
        .order('marked_hot_at', { ascending: false })
        .limit(limit);

      // Filter by tenant if specified (skip for 'all')
      if (selectedTenant && selectedTenant !== 'all') {
        query = query.eq('tenant_id', selectedTenant);
      }

      // Filter by campaign if specified (skip for 'all')
      if (selectedCampaign && selectedCampaign !== 'all') {
        query = query.eq('campaign_id', selectedCampaign);
      }

      // Filter by channel. The UI only offers 'sms'|'web' today, so 'sms' here really means
      // "not web": it used to also require phone IS NOT NULL, which made a hot lead on any
      // channel identified another way (Messenger: channel='messenger' + external_id, no
      // phone, no session_id) INVISIBLE on both tabs at once — not a filtering choice, a
      // gap. A future channel-specific tab can narrow this further; until one exists, "not
      // web" is the correct default so no hot lead is silently dropped from the queue.
      // Mirrors src/api_routes/priority-queue.js — same query, same fix, both places.
      if (channel === 'web') {
        query = query.not('session_id', 'is', null);
      } else {
        query = query.is('session_id', null);
      }

      // Filter by date range on marked_hot_at
      if (rangeStart) query = query.gte('marked_hot_at', rangeStart);
      if (rangeEnd) query = query.lt('marked_hot_at', rangeEnd);

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setQueue((data || []).filter(lead => !isDismissed(lead)).map(transformLead));
    } catch (err) {
      console.error('Error fetching priority queue:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, limit, dateRange, channel, selectedCampaign, tenantTz]);

  // Initial fetch
  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Real-time subscription
  useEffect(() => {
    const channelName = `priority-queue-${selectedTenant || 'all'}`;

    let channelConfig = {
      event: '*',
      schema: 'public',
      table: 'leads'
    };

    if (selectedTenant && selectedTenant !== 'all') {
      channelConfig.filter = `tenant_id=eq.${selectedTenant}`;
    }

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', channelConfig, (payload) => {
        console.log('🔥 Priority queue received:', payload.eventType, payload.new);
        const lead = payload.new;

        const TERMINAL_STATUSES = ['Lost', 'Won', 'Disqualified', 'dead'];
        const isTerminal = TERMINAL_STATUSES.includes(lead?.status);
        const belongsInQueue = (lead?.status === 'Hot Lead' || lead?.latest_judge_is_hot || isHandoff(lead)) && !isTerminal && !isDismissed(lead);

        if (belongsInQueue) {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            setQueue(prev => {
              const isExisting = prev.some(item => item.id === lead.id);
              const filtered = prev.filter(item => item.id !== lead.id);
              const transformed = transformLead(lead);
              transformed.isNew = !isExisting;

              // New leads always go to top first (skip sorting)
              return [transformed, ...filtered].slice(0, limit);
            });

            // Re-sort after animation ends (5 seconds)
            setTimeout(() => {
              setQueue(prev =>
                prev
                  .map(item => item.id === lead.id ? { ...item, isNew: false } : item)
                  .sort((a, b) => asMs(b.markedHotAt) - asMs(a.markedHotAt))
              );
            }, 5000);
          }
        } else if (payload.eventType === 'UPDATE') {
          // Remove if no longer hot OR if it's a terminal status
          setQueue(prev => prev.filter(item => item.id !== lead.id));
        }
      })
      .subscribe((status) => {
        console.log('📡 Priority queue subscription status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedTenant, limit]);

  // X off the queue. Persisted on the lead so it stays hidden across refreshes and
  // devices; a NEW inbound reply (isDismissed) brings it back. Optimistic-remove so
  // the card disappears instantly; on write failure it comes back on the next fetch.
  const dismissLead = useCallback(async (leadId) => {
    setQueue(prev => prev.filter(item => item.id !== leadId));
    const { error: dismissError } = await supabase
      .from('leads')
      .update({ priority_queue_dismissed_at: new Date().toISOString() })
      .eq('id', leadId);
    if (dismissError) {
      console.error('Failed to dismiss lead from priority queue:', dismissError);
    }
  }, []);

  return { queue, loading, error, refetch: fetchQueue, dismissLead };
};

export default usePriorityQueue;
